import type { OrganizationAssets } from "@frontend/lib/graphql/generated-gql";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useMemo } from "react";
import {
  calculateDistance,
  getLocationCoordinates,
  useZipCodeGeocode,
} from "../geocoding-utils";
import {
  type CameraDetail,
  type Location,
  LocationList,
  LocationMap,
  hasAnyMapCoordinates,
} from "./LocationSelector";

const API_KEY = process.env.NEXT_PUBLIC_GCP_MAPS_API as string;

interface LocationListWithDistanceProps {
  experityBooking: boolean;
  organizationAssets?: OrganizationAssets | null | undefined;
  zipCode: string;
  locations: Location[];
  selectedPracticeId: number;
  onSelectLocation: (id: number) => void;
  onLocationFocus: (ev: { detail: CameraDetail }) => void;
  cameraProps?: CameraDetail;
  onCameraChange: (ev: { detail: CameraDetail }) => void;
}

export function LocationListWithDistance({
  experityBooking,
  organizationAssets,
  zipCode,
  locations,
  selectedPracticeId,
  onSelectLocation,
  onLocationFocus,
  cameraProps,
  onCameraChange,
}: LocationListWithDistanceProps) {
  const showMap = hasAnyMapCoordinates(locations);

  return (
    <div className="w-full flex lg:flex-row flex-col-reverse sm:justify-between border border-[#E1E1E1] rounded-2xl h-[400px] lg:h-[400px]">
      <APIProvider apiKey={API_KEY} version="quarterly">
        <LocationListWithGeocodingAndDistance
          experityBooking={experityBooking}
          organizationAssets={organizationAssets}
          zipCode={zipCode}
          locations={locations}
          selectedPracticeId={selectedPracticeId}
          onSelectLocation={onSelectLocation}
          onLocationFocus={onLocationFocus}
          cameraProps={cameraProps}
          onCameraChange={onCameraChange}
          showMap={showMap}
        />
      </APIProvider>
    </div>
  );
}

interface LocationListWithGeocodingAndDistanceProps {
  experityBooking: boolean;
  organizationAssets?: OrganizationAssets | null | undefined;
  zipCode: string;
  locations: Location[];
  selectedPracticeId: number;
  onSelectLocation: (id: number) => void;
  onLocationFocus: (ev: { detail: CameraDetail }) => void;
  cameraProps?: CameraDetail;
  onCameraChange: (ev: { detail: CameraDetail }) => void;
  showMap: boolean;
}

function LocationListWithGeocodingAndDistance({
  experityBooking,
  organizationAssets,
  zipCode,
  locations,
  selectedPracticeId,
  onSelectLocation,
  onLocationFocus,
  cameraProps,
  onCameraChange,
  showMap,
}: LocationListWithGeocodingAndDistanceProps) {
  // Get zip code coordinates for distance calculation, this hook MUST be inside APIProvider
  const zipCodeLocation = useZipCodeGeocode(zipCode);
  const zipCodeCoordinates = zipCodeLocation
    ? { lat: zipCodeLocation.lat, lng: zipCodeLocation.lng }
    : null;

  // Calculate distances for all locations and sort by distance, expensive operations so good to memoize
  const locationsWithDistance = useMemo(() => {
    const locationsWithDist = locations.map((location) => {
      const locationCoords = getLocationCoordinates(location.googleMapsEmbed);
      const distance =
        experityBooking && zipCodeCoordinates && locationCoords
          ? calculateDistance(zipCodeCoordinates, locationCoords)
          : undefined;

      return {
        ...location,
        distance,
      };
    });

    // Sort by distance (undefined distances go to the end if any are undefined)
    return locationsWithDist.sort((a, b) => {
      if (a.distance === undefined && b.distance === undefined) return 0;
      if (a.distance === undefined) return 1;
      if (b.distance === undefined) return -1;
      return a.distance - b.distance;
    });
  }, [locations, experityBooking, zipCodeCoordinates]);

  return (
    <>
      <LocationList
        experityBooking={experityBooking}
        organizationAssets={organizationAssets}
        zipCode={zipCode}
        locations={locationsWithDistance}
        selectedPracticeId={selectedPracticeId}
        onSelectLocation={onSelectLocation}
        onLocationFocus={onLocationFocus}
        disableAutoSelect={true}
        showMap={showMap}
      />
      {showMap && (
        <LocationMap
          zipCode={zipCode}
          locations={locationsWithDistance}
          cameraProps={cameraProps}
          onCameraChange={onCameraChange}
          onSelectLocation={onSelectLocation}
        />
      )}
    </>
  );
}
